import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, ScanSearch, Coins, ShieldCheck, Plus, Check, ArrowRight, Wand2, Library } from "lucide-react";

const COST = 6;
const CAT = {
  name_variant: { label: "Name / spelling", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  equipment: { label: "Equipment / object", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  number_fact: { label: "Number / fact", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  pronoun: { label: "Pronoun drift", cls: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30" },
  role_relationship: { label: "Role / relationship", cls: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  status: { label: "Character status", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  timeline: { label: "Timeline", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};
const SEV = { high: "text-red-400", medium: "text-amber-400", low: "text-muted-foreground" };

export default function ContinuityDialog({ open, onOpenChange, projectId, onJump, onChanged, seriesOrder = 0 }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState({});          // findingId -> "replaced" | "codex"
  const [canonPick, setCanonPick] = useState({});        // findingId -> chosen canonical string
  const [confirm, setConfirm] = useState(null);          // {finding, pairs, label}
  const [numFix, setNumFix] = useState({});              // findingId -> {from,to}
  const [recon, setRecon] = useState({});                // findingId -> {chapter, correct}
  const [pronPick, setPronPick] = useState({});          // findingId -> chosen pronoun
  const [busyId, setBusyId] = useState(null);
  const inSaga = (seriesOrder || 0) > 1;
  const [sagaReport, setSagaReport] = useState(null);
  const [sagaLoading, setSagaLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/continuity/report`);
      if (data?.findings?.length) setReport(data);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => {
    if (open) { setReport(null); setResolved({}); setCanonPick({}); setNumFix({}); setConfirm(null); load(); }
  }, [open, load]);

  const loadSaga = useCallback(async () => {
    if (!projectId || !inSaga) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/saga-continuity/report`);
      if (data?.findings?.length || data?.checked_against?.length) setSagaReport(data);
    } catch { /* ignore */ }
  }, [projectId, inSaga]);

  useEffect(() => { if (open && inSaga) { setSagaReport(null); loadSaga(); } }, [open, inSaga, loadSaga]);

  const runSagaScan = async () => {
    setSagaLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/saga-continuity/scan`);
      setSagaReport(data);
      onChanged?.();
      toast.success(data.findings?.length ? `Found ${data.findings.length} cross-book issue(s) to review` : "No contradictions with the earlier books — consistent!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Saga continuity scan failed — please try again");
    } finally { setSagaLoading(false); }
  };

  const resolveSaga = async (fid, val) => {
    setSagaReport((r) => ({ ...r, findings: r.findings.map((f) => f.id === fid ? { ...f, resolved: val } : f) }));
    try { await api.post(`/projects/${projectId}/saga-continuity/resolve`, { finding_id: fid, resolved: val }); } catch { /* ignore */ }
  };

  const runScan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/continuity/scan`);
      setReport(data); setResolved({}); setCanonPick({});
      onChanged?.();
      toast.success(data.findings?.length ? `Found ${data.findings.length} thing(s) to review` : "No continuity issues found — nice and tight!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Continuity scan failed — please try again");
    } finally { setLoading(false); }
  };

  const chapterId = (title) => (report?.chapters || []).find((c) => c.title === title)?.id;

  const canonicalFor = (f) => {
    if (canonPick[f.id]) return canonPick[f.id];
    if (f.canonical) return f.canonical;
    const top = [...(f.variants || [])].sort((a, b) => b.count - a.count)[0];
    return top?.value || "";
  };

  const doReplace = async (f, pairs) => {
    setBusyId(f.id);
    let total = 0;
    try {
      for (const p of (pairs || [])) {
        if (!p.from || !p.to || p.from === p.to) continue;
        const { data } = await api.post(`/projects/${projectId}/continuity/apply`,
          { action: "replace", find: p.from, replace: p.to, finding_id: f.id });
        total += data.replaced || 0;
      }
      setResolved((r) => ({ ...r, [f.id]: "replaced" }));
      onChanged?.();
      toast.success(`Replaced ${total} occurrence(s). Old versions saved to History.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply the replacement");
    } finally { setBusyId(null); setConfirm(null); }
  };

  const doAutoReconcile = async (f) => {
    setBusyId(f.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/continuity/reconcile-auto`,
        { finding_id: f.id, detail: f.detail || f.title || "", chapters: f.chapters || [] });
      setResolved((r) => ({ ...r, [f.id]: "reconciled" }));
      onChanged?.();
      toast.success(`Fixed “${data.fixed_chapter}” → ${data.correct}. Old version saved to History.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't auto-reconcile — try the manual fix"));
    } finally { setBusyId(null); }
  };

  const doReconcile = async (f) => {
    const st = recon[f.id] || {};
    const title = st.chapter || (f.chapters || [])[0];
    const cid = chapterId(title);
    if (!cid) { toast.error("Pick which chapter to fix"); return; }
    if (!(st.correct || "").trim()) { toast.error("Type the correct version to lock in"); return; }
    setBusyId(f.id);
    try {
      await api.post(`/projects/${projectId}/continuity/reconcile`,
        { chapter_id: cid, correct: st.correct.trim(), finding_id: f.id });
      setResolved((r) => ({ ...r, [f.id]: "reconciled" }));
      onChanged?.();
      toast.success(`Fixed “${title}” to match — old version saved to History.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't reconcile — try again"));
    } finally { setBusyId(null); }
  };

  const addToCodex = async (f, name, kia, type = "character") => {
    if (!name) return;
    setBusyId(f.id);
    try {
      await api.post(`/projects/${projectId}/continuity/apply`,
        { action: "codex", name, type, description: f.detail || "", kia: kia ?? null, finding_id: f.id });
      setResolved((r) => ({ ...r, [f.id]: "codex" }));
      onChanged?.();
      toast.success(`“${name}” added to your Codex — future generations will lock to it.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add to Codex");
    } finally { setBusyId(null); }
  };

  const doFixPronoun = async (f, pronoun) => {
    const chapter_ids = (f.chapters || []).map(chapterId).filter(Boolean);
    if (!chapter_ids.length) { toast.error("No drifted chapters to fix"); return; }
    setBusyId(f.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/continuity/fix-pronoun`,
        { name: f.subject || canonicalFor(f), pronoun, chapter_ids, finding_id: f.id });
      setResolved((r) => ({ ...r, [f.id]: "reconciled" }));
      onChanged?.();
      toast.success(`Locked ${data.name} to ${data.pronoun} and fixed ${data.chapters_fixed} chapter(s). Codex updated; old versions saved to History.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.error === "out_of_credits" ? `Not enough credits (need ${d.needed})` : "Couldn't fix the pronoun — try again"));
    } finally { setBusyId(null); }
  };

  const findings = report?.findings || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange(v); }}>
      <DialogContent className="bg-popover max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="continuity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" /> Continuity check
          </DialogTitle>
        </DialogHeader>

        {inSaga && (
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5 space-y-3" data-testid="saga-continuity">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Library className="w-4 h-4 text-primary" />
                <div>
                  <p className="text-sm font-semibold leading-none">Saga continuity</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Checks this book against the canon of the earlier books{sagaReport?.checked_against?.length ? ` (${sagaReport.checked_against.join(", ")})` : ""} — dead characters reappearing, changed looks, broken facts or timeline.
                  </p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={runSagaScan} disabled={sagaLoading} data-testid="saga-continuity-run" className="gap-1.5 shrink-0">
                {sagaLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />}
                {sagaReport ? `Re-check (${COST})` : `Check earlier books (${COST})`}
              </Button>
            </div>
            {sagaLoading && <p className="text-[11px] text-muted-foreground">Reading the earlier books and comparing — this can take a minute…</p>}
            {sagaReport && (
              <div className="space-y-2" data-testid="saga-continuity-findings">
                {(sagaReport.findings || []).length === 0 ? (
                  <p className="text-xs text-emerald-400 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> No contradictions with the earlier books — consistent!</p>
                ) : sagaReport.findings.map((f) => (
                  <div key={f.id} data-testid="saga-continuity-finding"
                    className={`rounded-lg border p-2.5 text-xs ${f.resolved ? "border-border bg-secondary/20 opacity-60" : "border-border bg-background"}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${SEV[f.severity] || "text-muted-foreground"}`}>{f.severity}</span>
                        <span className="text-[10px] rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{f.category}</span>
                        <span className="font-semibold truncate">{f.title}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1 shrink-0" data-testid="saga-continuity-resolve"
                        onClick={() => resolveSaga(f.id, !f.resolved)}>
                        <Check className="w-3 h-3" /> {f.resolved ? "Reviewed" : "Mark reviewed"}
                      </Button>
                    </div>
                    {f.established && <p className="text-foreground/80"><span className="text-muted-foreground">Earlier book:</span> {f.established}</p>}
                    {f.conflict && <p className="text-foreground/80 mt-0.5"><span className="text-red-400">This book:</span> {f.conflict}</p>}
                    {f.suggestion && <p className="mt-1 text-primary/90"><span className="text-muted-foreground">Fix:</span> {f.suggestion}</p>}
                    {(f.chapters || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {f.chapters.map((ch, i) => <span key={i} className="text-[10px] rounded bg-secondary px-1.5 py-0.5 text-muted-foreground">{ch}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!report ? (
          <div className="py-8 text-center space-y-4">
            <ScanSearch className="w-10 h-10 mx-auto text-primary" />
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Scans your whole manuscript for continuity slips: name &amp; spelling variants, equipment &amp; object
              renames, contradictory numbers or physical facts, pronoun drift (a character's pronouns changing
              mid-book), role and relationship drift, characters killed off then reappearing, and timeline issues.
              It reports findings — it never rewrites your prose on its own.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Coins className="w-3.5 h-3.5" /> {COST} credits per scan
            </div>
            <Button onClick={runScan} disabled={loading} data-testid="continuity-run" className="gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />}
              {loading ? "Scanning your manuscript…" : "Run continuity check"}
            </Button>
            {loading && <p className="text-[11px] text-muted-foreground">This can take up to a minute for a full book.</p>}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {findings.length ? `${findings.length} finding(s) to review` : "No continuity issues found — nice and tight!"}
              </p>
              <Button size="sm" variant="outline" onClick={runScan} disabled={loading} data-testid="continuity-rerun" className="gap-1.5">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />} Re-scan ({COST})
              </Button>
            </div>

            <div className="overflow-y-auto -mx-1 px-1 space-y-3 flex-1" data-testid="continuity-findings">
              {findings.map((f) => {
                const cat = CAT[f.category] || { label: f.category, cls: "bg-muted text-muted-foreground border-border" };
                const canonical = canonicalFor(f);
                const isDone = resolved[f.id];
                const hasVariants = (f.variants || []).length > 0;
                const bigFix = (f.variants || []).some((v) => v.count > 10);
                const canNameReplace = (f.category === "name_variant" || f.category === "equipment") && hasVariants && canonical;
                const num = numFix[f.id] || { from: (f.variants || []).find((v) => v.value !== canonical)?.value || (f.variants?.[0]?.value || ""), to: canonical || "" };
                return (
                  <div key={f.id} data-testid="continuity-finding"
                    className={`rounded-lg border border-border bg-secondary/30 p-3 space-y-2 ${isDone ? "opacity-60" : ""}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`text-[10px] ${cat.cls}`}>{cat.label}</Badge>
                      <span className={`text-[10px] font-semibold uppercase ${SEV[f.severity] || ""}`}>{f.severity}</span>
                      <span className="font-medium text-sm">{f.title}</span>
                      {isDone && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30 gap-1"><Check className="w-3 h-3" />{isDone === "codex" ? "Added" : "Fixed"}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{f.detail}</p>

                    {hasVariants && (
                      <div className="flex flex-wrap gap-1.5">
                        {f.variants.map((v) => (
                          <button key={v.value} type="button" onClick={() => setCanonPick((p) => ({ ...p, [f.id]: v.value }))}
                            className={`text-[11px] rounded-md border px-2 py-1 transition-colors ${v.value === canonical ? "bg-primary/15 border-primary text-primary" : "border-border hover:border-primary/50"}`}>
                            “{v.value}” <span className="opacity-60">×{v.count}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {hasVariants && canonical && !isDone && (
                      <p className="text-[11px] text-muted-foreground">Keep <b className="text-foreground">“{canonical}”</b> as the correct one (tap a chip to change).</p>
                    )}

                    {(f.chapters || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {f.chapters.map((t) => {
                          const cid = chapterId(t);
                          return (
                            <button key={t} type="button" disabled={!cid} onClick={() => cid && onJump?.(cid)}
                              className="text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground hover:text-foreground hover:border-primary/50 disabled:opacity-50">
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {!isDone && (
                      <div className="space-y-2 pt-1">
                        {f.category === "pronoun" ? (
                          <div className="space-y-2 rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 p-2" data-testid="continuity-pronoun-fix">
                            <p className="text-[11px] text-muted-foreground">
                              Lock <b className="text-foreground">{f.subject || canonical}</b> to one pronoun for the whole book. I'll pin it in your Codex and rewrite the {(f.chapters || []).length} drifted chapter(s) to match, changing as little else as possible.
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {["he/him", "she/her", "they/them"].map((p) => {
                                const sel = (pronPick[f.id] || canonical) === p;
                                return (
                                  <button key={p} type="button" data-testid={`pronoun-opt-${p.replace("/", "-")}`}
                                    onClick={() => setPronPick((s) => ({ ...s, [f.id]: p }))}
                                    className={`text-[11px] rounded-md border px-2 py-1 transition-colors ${sel ? "bg-primary/15 border-primary text-primary" : "border-border hover:border-primary/50"}`}>
                                    {p}
                                  </button>
                                );
                              })}
                            </div>
                            <Button size="sm" className="h-7 gap-1.5 text-xs" data-testid="pronoun-lock-fix"
                              disabled={busyId === f.id}
                              onClick={() => doFixPronoun(f, pronPick[f.id] || canonical)}>
                              {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                              Lock &amp; fix across book ({Math.max(1, (f.chapters || []).length) * 4})
                            </Button>
                          </div>
                        ) : (
                          <>
                            {f.category === "number_fact" && (
                              <div className="flex items-end gap-2 flex-wrap" data-testid="continuity-num-fix">
                                <div className="space-y-1">
                                  <label className="text-[10px] text-muted-foreground block">Find (exact text)</label>
                                  <Input className="h-7 w-40 text-xs" value={num.from} data-testid="continuity-num-from"
                                    onChange={(e) => setNumFix((s) => ({ ...s, [f.id]: { ...num, from: e.target.value } }))} />
                                </div>
                                <ArrowRight className="w-3.5 h-3.5 mb-2 text-muted-foreground" />
                                <div className="space-y-1">
                                  <label className="text-[10px] text-muted-foreground block">Replace with</label>
                                  <Input className="h-7 w-40 text-xs" value={num.to} data-testid="continuity-num-to"
                                    onChange={(e) => setNumFix((s) => ({ ...s, [f.id]: { ...num, to: e.target.value } }))} />
                                </div>
                                <Button size="sm" className="h-7 gap-1.5 text-xs" data-testid="continuity-num-replace"
                                  disabled={busyId === f.id || !num.from || !num.to || num.from === num.to}
                                  onClick={() => setConfirm({ finding: f, label: `“${num.to}”`, pairs: [{ from: num.from, to: num.to }] })}>
                                  {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />} Replace all
                                  {bigFix && <span className="ml-1 rounded bg-background/30 px-1 text-[9px]">recommended</span>}
                                </Button>
                              </div>
                            )}
                            {!hasVariants && f.category !== "number_fact" && (f.chapters || []).length > 0 && (
                              <div className="space-y-1.5 rounded-md border border-violet-500/30 bg-violet-500/5 p-2" data-testid="continuity-reconcile">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-[11px] text-muted-foreground flex-1">Let AI fix this — it picks the version established earliest as correct and revises the later chapter to match, changing as little else as possible.</p>
                                  <Button size="sm" className="h-7 gap-1.5 text-xs shrink-0" data-testid="reconcile-auto"
                                    disabled={busyId === f.id} onClick={() => doAutoReconcile(f)}>
                                    {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Auto-fix with AI (4)
                                  </Button>
                                </div>
                                <details className="group">
                                  <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground select-none" data-testid="reconcile-manual-toggle">or set the correct version myself</summary>
                                  <div className="flex flex-wrap items-end gap-2 mt-1.5">
                                    <div className="space-y-1">
                                      <label className="text-[10px] text-muted-foreground block">Chapter to fix</label>
                                      <select className="h-7 rounded-md border border-border bg-background px-2 text-xs" data-testid="reconcile-chapter"
                                        value={(recon[f.id]?.chapter) || (f.chapters[0] || "")}
                                        onChange={(e) => setRecon((s) => ({ ...s, [f.id]: { ...(s[f.id] || {}), chapter: e.target.value } }))}>
                                        {f.chapters.map((t) => <option key={t} value={t}>{t}</option>)}
                                      </select>
                                    </div>
                                    <div className="space-y-1 flex-1 min-w-[200px]">
                                      <label className="text-[10px] text-muted-foreground block">Correct version to lock in</label>
                                      <Input className="h-7 text-xs" data-testid="reconcile-correct"
                                        placeholder="e.g. the victim was found on her back, arms at her sides, palms up"
                                        value={recon[f.id]?.correct || ""}
                                        onChange={(e) => setRecon((s) => ({ ...s, [f.id]: { ...(s[f.id] || {}), correct: e.target.value } }))} />
                                    </div>
                                    <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" data-testid="reconcile-apply"
                                      disabled={busyId === f.id} onClick={() => doReconcile(f)}>
                                      {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Fix (4)
                                    </Button>
                                  </div>
                                </details>
                              </div>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {canNameReplace && (
                                <Button size="sm" className="h-7 gap-1.5 text-xs" data-testid="continuity-replace"
                                  disabled={busyId === f.id}
                                  onClick={() => setConfirm({ finding: f, label: `“${canonical}”`, pairs: (f.variants || []).filter((v) => v.value !== canonical).map((v) => ({ from: v.value, to: canonical })) })}>
                                  {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                                  Replace all → “{canonical}”
                                  {bigFix && <span className="ml-1 rounded bg-background/30 px-1 text-[9px]">recommended</span>}
                                </Button>
                              )}
                              {canonical && (
                                <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" data-testid="continuity-codex"
                                  disabled={busyId === f.id} onClick={() => addToCodex(f, canonical, f.category === "status" ? true : null, f.category === "equipment" ? "object" : "character")}>
                                  <Plus className="w-3.5 h-3.5" /> Add “{canonical}” to Codex
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
          <AlertDialogContent className="bg-popover">
            <AlertDialogHeader>
              <AlertDialogTitle>Replace across the whole book?</AlertDialogTitle>
              <AlertDialogDescription>
                This will replace the text with <b>{confirm?.label}</b> across all chapters.
                Each affected chapter is snapshotted to History first, so you can undo it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => doReplace(confirm.finding, confirm.pairs)}>Replace all</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
